import { isMobile } from 'react-device-detect';
import { createState } from '../../lib/create-state';
import { meta } from './meta';
import {
    fetchScreenConfig,
    ScreenConfigRequestError,
} from '../../lib/screen-config';
import { current } from './game';

export type CurrencyType = 'game' | 'player';

export type ContentfulType = {
    spaceId: string;
    environmentId: string;
    accessToken: string;
};

export type SentryType = {
    trace: string;
    dsn: string;
};

export type SiteDataType = {
    contentful: ContentfulType;
    sentry: SentryType;
};

export type SiteType = {
    siteCode: string;
    siteId: number;
    homeUrl: string;
    helpUrl: string;
    responsibleUrl: string;
    data: SiteDataType;
};

export type DisplayType = {
    enableAutoBuyLossLimit: boolean;
    showTotalTickets: boolean;
    defaultCurrency: CurrencyType;
    enableCurrencyToggle: boolean;
    enableFreeBuyConfirm: boolean;
    enableUnlimitedAutoBuy: boolean;
    enableMaxCardAutoBuy: boolean;
    autoBuyLossLimitAmount: number;
    autoBuyGameLimit: number;
};

export type RoomType = {
    skin: string;
    announcer: string;
    caller: string;
    locale: string;
    localeId: number;
    gameId: string;
};

export type ScreenConfigType = {
    site: SiteType;
    display: DisplayType;
    room: RoomType;
    casinoAssetsURL: string;
};

export type SideGamesType = {
    name: string;
    type: string;
    configId: number;
    style: string;
    launch: string;
};

export type ScreenConfigState = {
    env: string;
    screenConfig: ScreenConfigType;
    sideGames: SideGamesType[];
    language: string;
};

export const DEFAULT_SCREEN_CONFIG_STATE: ScreenConfigState = {
    env: '',
    screenConfig: {
        site: {
            siteCode: '',
            siteId: 0,
            homeUrl: '',
            helpUrl: '',
            responsibleUrl: '',
            data: {} as SiteDataType,
        },
        display: {
            enableAutoBuyLossLimit: false,
            showTotalTickets: false,
            defaultCurrency: 'game',
            enableCurrencyToggle: false,
            enableFreeBuyConfirm: false,
            enableUnlimitedAutoBuy: true,
            enableMaxCardAutoBuy: true,
            autoBuyLossLimitAmount: 100.0,
            autoBuyGameLimit: 10,
        },
        room: {
            skin: '',
            announcer: '',
            caller: '',
            locale: '',
            localeId: 0,
            gameId: '',
        },
        casinoAssetsURL: '',
    },
    sideGames: [],
    language: '',
};

export const [screenConfig, resetScreenConfig] = createState<ScreenConfigState>(
    DEFAULT_SCREEN_CONFIG_STATE
);

export const getScreenConfig = async () => {
    meta.loaders.screenConfig.set(true);

    try {
        const searchParams = new URLSearchParams(window.location.search);

        if (isMobile) {
            searchParams.set('MOBILE', 'true');
        }

        const newConfig = (await fetchScreenConfig()) as ScreenConfigState;

        screenConfig.set(newConfig);

        meta.loaded.screenConfig.set(true);
    } catch (error) {
        meta.modals.msgCommon.set({
            name:
                error instanceof ScreenConfigRequestError &&
                error.status === 401
                    ? 'error.not.authenticated'
                    : 'error.general',
            isOpen: true,
        });
    }

    meta.loaders.screenConfig.set(false);
};

export const toggleCurrency = () => {
    const currencyType =
        screenConfig.screenConfig.display.defaultCurrency.value;

    const [currency, newCurrencyType] =
        currencyType === 'game'
            ? [current.player_currency.value, 'player' as CurrencyType]
            : [current.game_currency.value, 'game' as CurrencyType];

    screenConfig.screenConfig.display.defaultCurrency.set(newCurrencyType);
    meta.currency.set(currency);
};
